// src/routes/soa/index.tsx
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { riskStore } from '../../stores/riskStore';
import { soaStore, soaActions } from '../../stores/soaStore';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';

import { ModuleHeader } from '../../components/layout/ModuleHeader';
import { Icon } from '../../components/ui/Icon';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { iso27001Controls, controlThemes } from '../../data/iso27001Controls';
import { formatDate, formatToday } from '../../utils/dateUtils';
import { exportToCSV, downloadBlob } from '../../utils/exportUtils';
import { adminStore, getImplementationWording, getJustificationWording } from '../../stores/adminStore';
import { AiSuggestButton } from '../../components/ui/AiSuggestButton';
import { DocumentVersionModal } from '../../components/shared/DocumentVersionModal';
import type { Risk } from '../../types/risk';
import type { ISO27001Control } from '../../types/soa';

export const Route = createFileRoute('/soa/')({
  component: SoAModule,
});

function SoAModule() {
  const riskSnap = useSnapshot(riskStore);
  const soaSnap = useSnapshot(soaStore);
  const adminSnap = useSnapshot(adminStore);
  const { showToast, ToastContainer } = useToast();

  const [showViewModal, setShowViewModal] = useState(false);
  const [showJustificationModal, setShowJustificationModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCompanyInfoModal, setShowCompanyInfoModal] = useState(false);
  const [showWordingPicker, setShowWordingPicker] = useState(false);
  const [wordingPickerType, setWordingPickerType] = useState<'implementation' | 'justification'>('justification');
  const [selectedRisk, setSelectedRisk] = useState<Risk | null>(null);
  const [currentControl, setCurrentControl] = useState<ISO27001Control | null>(null);
  const [justificationText, setJustificationText] = useState('');

  // Document version form state is now in the shared DocumentVersionModal component

  // Get risks using each control (checks multi-control array with flat field fallback)
  const getRisksForControl = (controlText: string) => {
    return (riskSnap.risks || []).filter((r) =>
      r.controls?.some(c => c.control === controlText) || r.control === controlText
    );
  };

  // Get implementation status for a control based on linked risks
  const getControlStatus = (controlText: string) => {
    const risks = getRisksForControl(controlText);
    if (risks.length === 0) return { status: 'Not Applicable', color: 'gray' };
    
    const statuses = risks.map((r) => r.controlStatus).filter(Boolean);
    if (statuses.length === 0) return { status: 'Not Started', color: 'gray' };
    
    if (statuses.every((s) => s === 'Implemented')) {
      return { status: 'Implemented', color: 'green' };
    } else if (statuses.some((s) => s === 'Implemented' || s === 'In Progress')) {
      return { status: 'In Progress', color: 'blue' };
    } else if (statuses.some((s) => s === 'Under Review')) {
      return { status: 'Under Review', color: 'yellow' };
    }
    return { status: 'Not Started', color: 'gray' };
  };

  // Calculate used and unused controls
  const usedControls = useMemo(() => {
    return iso27001Controls.filter((control) => {
      return getRisksForControl(control.control).length > 0;
    });
  }, [riskSnap.risks]);

  const unusedControls = useMemo(() => {
    return iso27001Controls.filter((control) => {
      return getRisksForControl(control.control).length === 0;
    });
  }, [riskSnap.risks]);

  // Calculate stats
  const stats = useMemo(
    () => ({
      totalControls: iso27001Controls.length,
      usedControls: usedControls.length,
      unusedControls: unusedControls.length,
      coveragePercent: ((usedControls.length / iso27001Controls.length) * 100).toFixed(1),
      justifiedCount: Object.keys(soaSnap.justifications).length,
      implementedCount: usedControls.filter((c) => getControlStatus(c.control).status === 'Implemented').length,
    }),
    [usedControls, unusedControls, soaSnap.justifications, riskSnap.risks]
  );

  // Filter controls
  const getFilteredControls = (controls: ISO27001Control[]) => {
    let filtered = controls;

    if (soaSnap.filterSection !== 'all') {
      filtered = filtered.filter((c) => c.theme === soaSnap.filterSection);
    }

    if (soaSnap.searchTerm) {
      const term = soaSnap.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.control.toLowerCase().includes(term) ||
          c.objective.toLowerCase().includes(term) ||
          getRisksForControl(c.control).some((r) =>
            r.referenceNumber?.toLowerCase().includes(term)
          )
      );
    }

    return filtered;
  };

  const openJustificationModal = (control: ISO27001Control) => {
    setCurrentControl(control);
    setJustificationText(soaSnap.justifications[control.control]?.justification || '');
    setShowJustificationModal(true);
  };

  const saveJustification = () => {
    if (!currentControl) return;
    soaActions.setJustification(currentControl.control, justificationText);
    setShowJustificationModal(false);
    showToast('Justification saved', 'success');
  };

  const deleteJustification = (controlName: string) => {
    if (confirm('Are you sure you want to delete this justification?')) {
      soaActions.deleteJustification(controlName);
      showToast('Justification deleted', 'success');
    }
  };

  const openViewModal = (risk: Risk) => {
    setSelectedRisk(risk);
    setShowViewModal(true);
  };

  // saveCompanyInfo and incrementVersion are now in the shared DocumentVersionModal component

  // Export to CSV with document metadata header
  const handleExportCSV = () => {
    const escCSV = (val: string) => `"${(val || '').replace(/"/g, '""')}"`;
    const today = formatToday();

    // Metadata rows
    const metaRows: string[] = [
      escCSV('Statement of Applicability'),
      `${escCSV('Company')},${escCSV(soaSnap.companyInfo.companyName)}`,
      `${escCSV('Issue No')},${escCSV(soaSnap.companyInfo.issueNo)},${escCSV('Date')},${escCSV(soaSnap.companyInfo.date || today)}`,
      `${escCSV('Prepared By')},${escCSV(soaSnap.companyInfo.preparedBy || '')},${escCSV('Reviewed By')},${escCSV(soaSnap.companyInfo.reviewedBy || '')}`,
      `${escCSV('Approved By')},${escCSV(soaSnap.companyInfo.approvedBy || '')},${escCSV('Approved Date')},${escCSV(soaSnap.companyInfo.approvedDate ? formatDate(soaSnap.companyInfo.approvedDate) : '')}`,
      `${escCSV('Next Review')},${escCSV(soaSnap.companyInfo.nextReviewDate ? formatDate(soaSnap.companyInfo.nextReviewDate) : '')}`,
      '', // Blank separator row
    ];

    // Control data headers and rows
    const headers = ['Theme', 'Control', 'Objective', 'Applicable', 'Risks', 'Control Implementation', 'Status', 'Last Reviewed'];
    const dataRows = iso27001Controls.map((control) => {
      const controlRisks = getRisksForControl(control.control);
      const isUsed = controlRisks.length > 0;
      const justification = soaSnap.justifications[control.control];

      const riskRefs = isUsed
        ? controlRisks.map((r) => r.referenceNumber).join('\n')
        : '-';
      const implementations = isUsed
        ? controlRisks.map((r) => {
            // Show the matching control entry's implementation if multi-control
            const entry = r.controls?.find(c => c.control === control.control);
            return entry?.controlImplementation || r.controlImplementation || '-';
          }).join('\n')
        : justification?.justification || 'This control has not been identified as managing any risks';
      const statuses = isUsed
        ? controlRisks.map((r) => r.controlStatus || 'Not Started').join('\n')
        : 'Not Applicable';
      const dates = controlRisks.map((r) => r.lastDateReviewed).filter(Boolean);

      return [
        control.theme,
        control.control,
        control.objective,
        isUsed ? 'Yes' : 'No',
        riskRefs,
        implementations,
        statuses,
        dates[0] ? formatDate(dates[0]) : '',
      ].map(escCSV).join(',');
    });

    const csv = [...metaRows, headers.join(','), ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `statement-of-applicability-${new Date().toISOString().split('T')[0]}.csv`);
    showToast('CSV exported successfully', 'success');
  };

  // Print the SoA (opens print dialog)
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('Please allow popups to print', 'error');
      return;
    }

    const htmlContent = generatePDFContent();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Export to PDF (downloads actual PDF file)
  const handleExportPDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const today = formatToday();

    // Logo (if set)
    let contentStartX = 14;
    if (soaSnap.companyInfo.logo) {
      try {
        const img = new Image();
        img.src = soaSnap.companyInfo.logo;
        const logoHeight = 20; // mm
        const logoWidth = img.naturalWidth && img.naturalHeight
          ? (img.naturalWidth / img.naturalHeight) * logoHeight
          : logoHeight; // square fallback
        const format = soaSnap.companyInfo.logo.includes('image/png') ? 'PNG' : 'JPEG';
        doc.addImage(soaSnap.companyInfo.logo, format, 14, 6, logoWidth, logoHeight);
        contentStartX = 14 + logoWidth + 6;
      } catch {
        // Continue without logo if it fails
      }
    }

    // Title
    doc.setFontSize(20);
    doc.setTextColor(79, 70, 229); // Indigo
    doc.text('Statement of Applicability', pageWidth / 2, 15, { align: 'center' });

    // Company name
    doc.setFontSize(14);
    doc.setTextColor(100, 100, 100);
    doc.text(soaSnap.companyInfo.companyName, pageWidth / 2, 22, { align: 'center' });

    // Meta info
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Issue No: ' + soaSnap.companyInfo.issueNo, 14, 30);
    doc.text('Date: ' + (soaSnap.companyInfo.date || today), pageWidth - 14, 30, { align: 'right' });

    // Signatories line
    let signatoryY = 35;
    const sigParts: string[] = [];
    if (soaSnap.companyInfo.preparedBy) sigParts.push('Prepared By: ' + soaSnap.companyInfo.preparedBy);
    if (soaSnap.companyInfo.reviewedBy) sigParts.push('Reviewed By: ' + soaSnap.companyInfo.reviewedBy);
    if (soaSnap.companyInfo.approvedBy) {
      let approvedText = 'Approved By: ' + soaSnap.companyInfo.approvedBy;
      if (soaSnap.companyInfo.approvedDate) approvedText += ' (' + formatDate(soaSnap.companyInfo.approvedDate) + ')';
      sigParts.push(approvedText);
    }
    if (sigParts.length > 0) {
      doc.setFontSize(8);
      doc.text(sigParts.join('  |  '), 14, signatoryY);
      signatoryY += 5;
    }
    if (soaSnap.companyInfo.nextReviewDate) {
      doc.setFontSize(8);
      doc.text('Next Review: ' + formatDate(soaSnap.companyInfo.nextReviewDate), 14, signatoryY);
      signatoryY += 5;
    }

    // Stats
    doc.setFontSize(9);
    const statsY = signatoryY + 3;
    const statsSpacing = 50;
    doc.setTextColor(0, 0, 0);
    doc.text('Total: ' + stats.totalControls, 14, statsY);
    doc.text('Applicable: ' + stats.usedControls, 14 + statsSpacing, statsY);
    doc.text('Not Applicable: ' + stats.unusedControls, 14 + statsSpacing * 2, statsY);
    doc.text('Coverage: ' + stats.coveragePercent + '%', 14 + statsSpacing * 3, statsY);
    doc.text('Implemented: ' + stats.implementedCount, 14 + statsSpacing * 4, statsY);

    // Table data - create separate rows for each risk to ensure alignment
    const tableData: (string | { content: string; rowSpan?: number })[][] = [];
    
    iso27001Controls.forEach((control) => {
      const controlRisks = getRisksForControl(control.control);
      const isUsed = controlRisks.length > 0;
      const justification = soaSnap.justifications[control.control];
      
      if (isUsed && controlRisks.length > 0) {
        // First risk row includes control info with rowSpan
        controlRisks.forEach((risk, idx) => {
          const matchingEntry = risk.controls?.find(c => c.control === control.control);
          const impl = matchingEntry?.controlImplementation || risk.controlImplementation || '-';
          if (idx === 0) {
            tableData.push([
              { content: control.control, rowSpan: controlRisks.length },
              { content: control.objective.length > 80 ? control.objective.substring(0, 80) + '...' : control.objective, rowSpan: controlRisks.length },
              { content: 'Yes', rowSpan: controlRisks.length },
              risk.referenceNumber,
              impl,
              risk.controlStatus || 'Not Started',
            ]);
          } else {
            tableData.push([
              risk.referenceNumber,
              impl,
              risk.controlStatus || 'Not Started',
            ]);
          }
        });
      } else {
        // No risks - single row
        tableData.push([
          control.control,
          control.objective.length > 80 ? control.objective.substring(0, 80) + '...' : control.objective,
          'No',
          '-',
          justification?.justification || 'Not identified as managing any risks',
          'Not Applicable',
        ]);
      }
    });

    // Generate table
    autoTable(doc, {
      startY: statsY + 6,
      head: [['Control', 'Objective', 'Applicable', 'Risks', 'Control Implementation', 'Status']],
      body: tableData,
      styles: {
        fontSize: 7,
        cellPadding: 2,
        valign: 'top',
      },
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: 255,
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 40 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 25 },
        4: { cellWidth: 'auto' },
        5: { cellWidth: 25, halign: 'center' },
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
      didDrawPage: (data) => {
        // Footer
        const footerY = doc.internal.pageSize.getHeight() - 10;
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(
          'Created using Certain\u00AE \u2014 Asset Risk Manager',
          14,
          footerY,
          { align: 'left' }
        );
        doc.text(
          'Generated on ' + today + ' | ISO 27001:2022 Statement of Applicability',
          pageWidth / 2,
          footerY,
          { align: 'center' }
        );
        // Page number
        doc.text(
          'Page ' + doc.internal.pages.length,
          pageWidth - 14,
          footerY,
          { align: 'right' }
        );
      },
    });

    // Save the PDF
    const filename = 'statement-of-applicability-' + soaSnap.companyInfo.issueNo.replace(/\./g, '-') + '-' + new Date().toISOString().split('T')[0] + '.pdf';
    doc.save(filename);
    showToast('PDF exported successfully', 'success');
  };

  const generatePDFContent = () => {
    const today = formatToday();

    // The generated HTML is written to an about:blank print window where the
    // app's CSP does not apply, so any user-controlled value embedded here is a
    // stored-XSS sink. Escape every dynamic value before interpolation.
    const esc = (v: unknown) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    let controlRows = '';
    iso27001Controls.forEach((control) => {
      const controlRisks = getRisksForControl(control.control);
      const isUsed = controlRisks.length > 0;
      const justification = soaSnap.justifications[control.control];
      
      let riskCells = '';
      if (isUsed) {
        // Create nested table for aligned risks
        const riskRows = controlRisks.map((r) => {
          const matchingEntry = r.controls?.find(c => c.control === control.control);
          const impl = matchingEntry?.controlImplementation || r.controlImplementation || '-';
          const status = r.controlStatus || 'Not Started';
          const statusColor = status === 'Implemented' ? '#d1fae5' :
                             status === 'In Progress' ? '#dbeafe' :
                             status === 'Under Review' ? '#fef3c7' : '#f3f4f6';
          const statusTextColor = status === 'Implemented' ? '#065f46' :
                                 status === 'In Progress' ? '#1e40af' :
                                 status === 'Under Review' ? '#92400e' : '#374151';
          return `<tr>
            <td style="padding: 4px 8px 4px 0; vertical-align: top; width: 15%;">${esc(r.referenceNumber)}</td>
            <td style="padding: 4px 8px; vertical-align: top; width: 65%;">${esc(impl)}</td>
            <td style="padding: 4px 0 4px 8px; vertical-align: top; text-align: center; width: 20%;">
              <span style="padding: 2px 6px; border-radius: 4px; font-size: 9px; background: ${statusColor}; color: ${statusTextColor}; white-space: nowrap;">${esc(status)}</span>
            </td>
          </tr>`;
        }).join('');
        
        riskCells = `<td colspan="3" style="padding: 8px; border: 1px solid #ddd; font-size: 10px; vertical-align: top;">
          <table style="width: 100%; border-collapse: collapse;">
            <tbody>${riskRows}</tbody>
          </table>
        </td>`;
      } else {
        riskCells = `
          <td style="padding: 8px; border: 1px solid #ddd; font-size: 10px; vertical-align: top;">-</td>
          <td style="padding: 8px; border: 1px solid #ddd; font-size: 10px; vertical-align: top;">${esc(justification?.justification) || 'Not identified as managing any risks'}</td>
          <td style="padding: 8px; border: 1px solid #ddd; font-size: 10px; vertical-align: top; text-align: center;">
            <span style="padding: 2px 6px; border-radius: 4px; font-size: 9px; background: #f3f4f6; color: #374151;">Not Applicable</span>
          </td>
        `;
      }

      controlRows += `
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; font-size: 11px; vertical-align: top;">${esc(control.control)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; font-size: 10px; vertical-align: top;">${esc(control.objective)}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: center; vertical-align: top;">
            <span style="padding: 2px 8px; border-radius: 4px; font-size: 10px; background: ${isUsed ? '#d1fae5' : '#fee2e2'}; color: ${isUsed ? '#065f46' : '#991b1b'};">
              ${isUsed ? 'Yes' : 'No'}
            </span>
          </td>
          ${riskCells}
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Statement of Applicability - ${esc(soaSnap.companyInfo.companyName)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .header h1 { color: #4f46e5; margin-bottom: 5px; }
          .meta { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 12px; color: #666; }
          .stats { display: flex; gap: 20px; margin-bottom: 20px; }
          .stat { background: #f3f4f6; padding: 10px 15px; border-radius: 8px; }
          .stat-value { font-size: 24px; font-weight: bold; color: #4f46e5; }
          .stat-label { font-size: 11px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #4f46e5; color: white; padding: 10px 8px; text-align: left; font-size: 11px; border: 1px solid #ddd; }
          td { border: 1px solid #ddd; }
          tr:nth-child(even) { background: #f9fafb; }
          .theme-header { background: #e5e7eb; font-weight: bold; }
          .theme-header td { padding: 10px; font-size: 12px; }
          
          /* Nested table for risks - no inner borders */
          td table { border: none !important; }
          td table td { border: none !important; padding: 4px 8px !important; }
          
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
            
            /* Main table borders only */
            table { 
              border-collapse: collapse !important;
            }
            
            th, td {
              border: 1px solid #333 !important;
              padding: 8px !important;
            }
            
            /* Remove borders from nested tables */
            td table {
              border: none !important;
            }
            
            td table td {
              border: none !important;
              padding: 4px 8px !important;
            }
            
            th {
              background-color: #4f46e5 !important;
              color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            tr:nth-child(even) {
              background-color: #f9fafb !important;
              -webkit-print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${soaSnap.companyInfo.logo ? `<img src="${esc(soaSnap.companyInfo.logo)}" alt="Company logo" style="max-height: 60px; max-width: 200px; margin: 0 auto 10px;" />` : ''}
          <h1>Statement of Applicability</h1>
          <h2 style="color: #666; font-weight: normal;">${esc(soaSnap.companyInfo.companyName)}</h2>
        </div>
        
        <div class="meta">
          <div>Issue No: ${esc(soaSnap.companyInfo.issueNo)}</div>
          <div>Date: ${esc(soaSnap.companyInfo.date) || today}</div>
        </div>

        ${(soaSnap.companyInfo.preparedBy || soaSnap.companyInfo.reviewedBy || soaSnap.companyInfo.approvedBy) ? `
        <div style="font-size: 11px; color: #555; margin-bottom: 8px;">
          ${[
            soaSnap.companyInfo.preparedBy ? 'Prepared By: ' + esc(soaSnap.companyInfo.preparedBy) : '',
            soaSnap.companyInfo.reviewedBy ? 'Reviewed By: ' + esc(soaSnap.companyInfo.reviewedBy) : '',
            soaSnap.companyInfo.approvedBy ? 'Approved By: ' + esc(soaSnap.companyInfo.approvedBy) + (soaSnap.companyInfo.approvedDate ? ' (' + formatDate(soaSnap.companyInfo.approvedDate) + ')' : '') : '',
          ].filter(Boolean).join('&nbsp;&nbsp;|&nbsp;&nbsp;')}
        </div>` : ''}
        ${soaSnap.companyInfo.nextReviewDate ? `
        <div style="font-size: 11px; color: #555; margin-bottom: 12px;">
          Next Review: ${formatDate(soaSnap.companyInfo.nextReviewDate)}
        </div>` : ''}

        <div class="stats">
          <div class="stat">
            <div class="stat-value">${stats.totalControls}</div>
            <div class="stat-label">Total Controls</div>
          </div>
          <div class="stat">
            <div class="stat-value">${stats.usedControls}</div>
            <div class="stat-label">Applicable</div>
          </div>
          <div class="stat">
            <div class="stat-value">${stats.unusedControls}</div>
            <div class="stat-label">Not Applicable</div>
          </div>
          <div class="stat">
            <div class="stat-value">${stats.coveragePercent}%</div>
            <div class="stat-label">Coverage</div>
          </div>
          <div class="stat">
            <div class="stat-value">${stats.implementedCount}</div>
            <div class="stat-label">Implemented</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 12%;">Control</th>
              <th style="width: 20%;">Objective</th>
              <th style="width: 7%;">Applicable</th>
              <th style="width: 10%;">Risks</th>
              <th style="width: 36%;">Control Implementation</th>
              <th style="width: 15%;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${controlRows}
          </tbody>
        </table>

        <div style="margin-top: 30px; font-size: 10px; color: #666; display: flex; justify-content: space-between;">
          <span>Created using Certain® — Asset Risk Manager</span>
          <span>Generated on ${today} | ISO 27001:2022 Statement of Applicability</span>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      <NavigationSidebar />
      <div className="flex-1 overflow-auto min-w-0">
      <ModuleHeader
        title="Information Security Statement of Applicability"
        subtitle="Complete SoA with comprehensive tracking and justifications"
      />

      <div className="p-6">
        {/* Statistics Dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <p className="text-gray-500 text-sm font-medium">Total Controls</p>
            <p className="text-3xl font-bold text-gray-800 mt-1">{stats.totalControls}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <p className="text-gray-500 text-sm font-medium">Applicable</p>
            <p className="text-3xl font-bold text-green-600 mt-1">{stats.usedControls}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <p className="text-gray-500 text-sm font-medium">Not Applicable</p>
            <p className="text-3xl font-bold text-orange-600 mt-1">{stats.unusedControls}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <p className="text-gray-500 text-sm font-medium">Coverage</p>
            <p className="text-3xl font-bold text-purple-600 mt-1">{stats.coveragePercent}%</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <p className="text-gray-500 text-sm font-medium">Implemented</p>
            <p className="text-3xl font-bold text-blue-600 mt-1">{stats.implementedCount}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <p className="text-gray-500 text-sm font-medium">Justifications</p>
            <p className="text-3xl font-bold text-amber-600 mt-1">{stats.justifiedCount}</p>
          </motion.div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Icon
                  name="Search"
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search controls or risk number..."
                  value={soaSnap.searchTerm}
                  onChange={(e) => soaActions.setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <select
                value={soaSnap.filterSection}
                onChange={(e) => soaActions.setFilterSection(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Sections</option>
                {controlThemes.map((theme) => (
                  <option key={theme} value={theme}>
                    {theme}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={soaSnap.showUsed}
                  onChange={() => soaActions.toggleShowUsed()}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">Applicable</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={soaSnap.showUnused}
                  onChange={() => soaActions.toggleShowUnused()}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">Not Applicable</span>
              </label>
              <Button onClick={() => setShowCompanyInfoModal(true)} variant="secondary" size="sm">
                <Icon name="FileText" size={16} className="mr-1" />
                Document Version
              </Button>
              <Button onClick={() => setShowPreviewModal(true)} variant="secondary" size="sm">
                <Icon name="Eye" size={16} className="mr-1" />
                Preview
              </Button>
              <Button onClick={handleExportCSV} variant="secondary" size="sm">
                <Icon name="FileSpreadsheet" size={16} className="mr-1" />
                CSV
              </Button>
              <Button onClick={handleExportPDF} size="sm">
                <Icon name="FileText" size={16} className="mr-1" />
                PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Used Controls Section */}
        {soaSnap.showUsed && usedControls.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Applicable Controls ({usedControls.length})
            </h2>
            {controlThemes.map((theme) => {
              const themeControls = getFilteredControls(
                usedControls.filter((c) => c.theme === theme)
              );
              if (themeControls.length === 0) return null;

              return (
                <div key={theme} className="bg-white rounded-xl shadow-md p-6 mb-4">
                  <h3 className="text-xl font-bold text-purple-900 mb-4">{theme}</h3>
                  <div className="space-y-4">
                    {themeControls.map((control) => {
                      const controlRisks = getRisksForControl(control.control);
                      const status = getControlStatus(control.control);
                      return (
                        <ControlCard
                          key={control.control}
                          control={control}
                          risks={controlRisks as Risk[]}
                          isUsed={true}
                          status={status}
                          justification={soaSnap.justifications[control.control]?.justification}
                          onViewRisk={openViewModal}
                          onEditJustification={() => openJustificationModal(control)}
                          onDeleteJustification={() => deleteJustification(control.control)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Unused Controls Section */}
        {soaSnap.showUnused && unusedControls.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Not Applicable Controls ({unusedControls.length})
            </h2>
            {controlThemes.map((theme) => {
              const themeControls = getFilteredControls(
                unusedControls.filter((c) => c.theme === theme)
              );
              if (themeControls.length === 0) return null;

              return (
                <div key={theme} className="bg-white rounded-xl shadow-md p-6 mb-4">
                  <h3 className="text-xl font-bold text-gray-700 mb-4">{theme}</h3>
                  <div className="space-y-4">
                    {themeControls.map((control) => (
                      <ControlCard
                        key={control.control}
                        control={control}
                        risks={[]}
                        isUsed={false}
                        status={{ status: 'Not Applicable', color: 'gray' }}
                        justification={soaSnap.justifications[control.control]?.justification}
                        onEditJustification={() => openJustificationModal(control)}
                        onDeleteJustification={() => deleteJustification(control.control)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* View Risk Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={'Risk Details - ' + (selectedRisk?.referenceNumber || '')}
        size="xl"
        gradient="from-blue-600 to-indigo-600"
      >
        {selectedRisk && (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Reference and Basic Info */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium text-purple-600">Reference</label>
                  <p className="text-sm font-semibold text-gray-900">{selectedRisk.referenceNumber}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-purple-600">Risk Category</label>
                  <p className="text-sm text-gray-900">{selectedRisk.riskCategory.length > 0 ? selectedRisk.riskCategory.join(', ') : '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-purple-600">Risk Owner</label>
                  <p className="text-sm text-gray-900">{selectedRisk.riskOwner || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-purple-600">Control Status</label>
                  <p className="text-sm text-gray-900">{selectedRisk.controlStatus || '-'}</p>
                </div>
              </div>
            </div>

            {/* Asset Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Asset Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Asset Category</label>
                  <p className="text-sm text-gray-900">{selectedRisk.assetCategory || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Asset</label>
                  <p className="text-sm text-gray-900">{selectedRisk.asset || '-'}</p>
                </div>
                {selectedRisk.assetReferenceNumber && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Asset Reference</label>
                    <p className="text-sm text-gray-900">{selectedRisk.assetReferenceNumber}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Risk Details */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Risk Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Threat</label>
                  <p className="text-sm text-gray-900">{selectedRisk.threat || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Vulnerability</label>
                  <p className="text-sm text-gray-900">{selectedRisk.vulnerability || '-'}</p>
                </div>
              </div>
            </div>

            {/* Uncontrolled Risk Assessment */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Uncontrolled Risk</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Likelihood</label>
                  <p className="text-sm text-gray-900">{selectedRisk.ucLikelihood || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Impact</label>
                  <p className="text-sm text-gray-900">{selectedRisk.ucImpact || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Risk Score</label>
                  {(() => {
                    const likelihoodValues: Record<string, number> = { 'Rare': 1, 'Unlikely': 2, 'Possible': 3, 'Likely': 4, 'Almost Certain': 5 };
                    const impactValues: Record<string, number> = { 'Insignificant': 1, 'Minor': 2, 'Moderate': 3, 'Serious': 4, 'Catastrophic': 5 };
                    const score = (likelihoodValues[selectedRisk.ucLikelihood || ''] || 0) * (impactValues[selectedRisk.ucImpact || ''] || 0);
                    const color = score >= 15 ? 'bg-red-100 text-red-700' : score >= 10 ? 'bg-orange-100 text-orange-700' : score >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';
                    return <span className={`inline-block px-2 py-1 rounded text-sm font-semibold ${color}`}>{score || '-'}</span>;
                  })()}
                </div>
              </div>
            </div>

            {/* Treatment */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Treatment Strategy</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Treatment</label>
                  <p className="text-sm text-gray-900">{selectedRisk.treatment || '-'}</p>
                </div>
                {selectedRisk.treatmentDetail && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Treatment Detail</label>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedRisk.treatmentDetail}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Managed Risk Assessment */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Managed Risk</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Likelihood</label>
                  <p className="text-sm text-gray-900">{selectedRisk.managedLikelihood || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Impact</label>
                  <p className="text-sm text-gray-900">{selectedRisk.managedImpact || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Risk Score</label>
                  {(() => {
                    const likelihoodValues: Record<string, number> = { 'Rare': 1, 'Unlikely': 2, 'Possible': 3, 'Likely': 4, 'Almost Certain': 5 };
                    const impactValues: Record<string, number> = { 'Insignificant': 1, 'Minor': 2, 'Moderate': 3, 'Serious': 4, 'Catastrophic': 5 };
                    const score = (likelihoodValues[selectedRisk.managedLikelihood || ''] || 0) * (impactValues[selectedRisk.managedImpact || ''] || 0);
                    const color = score >= 15 ? 'bg-red-100 text-red-700' : score >= 10 ? 'bg-orange-100 text-orange-700' : score >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700';
                    return <span className={`inline-block px-2 py-1 rounded text-sm font-semibold ${color}`}>{score || '-'}</span>;
                  })()}
                </div>
              </div>
            </div>

            {/* Control Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Control Information</h4>
              <div className="space-y-4">
                {(() => {
                  const ctrls = selectedRisk.controls?.length > 0
                    ? selectedRisk.controls
                    : selectedRisk.control
                      ? [{ control: selectedRisk.control, controlImplementation: selectedRisk.controlImplementation }]
                      : [];
                  if (ctrls.length === 0) return <p className="text-sm text-gray-400">No controls assigned</p>;
                  return ctrls.map((ctrl, idx) => (
                    <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                      <label className="text-xs font-medium text-gray-500">Control {ctrls.length > 1 ? idx + 1 : ''}</label>
                      <p className="text-sm text-gray-900 font-medium">{ctrl.control || '-'}</p>
                      {ctrl.controlImplementation && (
                        <>
                          <label className="text-xs font-medium text-gray-500 mt-2 block">Implementation</label>
                          <p className="text-sm text-gray-900">{ctrl.controlImplementation}</p>
                        </>
                      )}
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Review Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Review Information</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Control Status</label>
                  <p className="text-sm text-gray-900">{selectedRisk.controlStatus || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Date Completed</label>
                  <p className="text-sm text-gray-900">
                    {selectedRisk.dateCompleted ? formatDate(selectedRisk.dateCompleted) : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Review Period</label>
                  <p className="text-sm text-gray-900">
                    {selectedRisk.maxReviewPeriod ? `${selectedRisk.maxReviewPeriod} months` : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Last Reviewed</label>
                  <p className="text-sm text-gray-900">
                    {selectedRisk.lastDateReviewed ? formatDate(selectedRisk.lastDateReviewed) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            {(selectedRisk.opportunities || selectedRisk.actions || selectedRisk.comments || selectedRisk.notes) && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">Additional Information</h4>
                <div className="space-y-4">
                  {selectedRisk.opportunities && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Opportunities</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedRisk.opportunities}</p>
                    </div>
                  )}
                  {selectedRisk.actions && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Actions</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedRisk.actions}</p>
                    </div>
                  )}
                  {selectedRisk.comments && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Comments</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedRisk.comments}</p>
                    </div>
                  )}
                  {selectedRisk.notes && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Notes</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedRisk.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className="text-xs text-gray-400 pt-4 border-t">
              <div className="flex justify-between">
                <span>Created: {selectedRisk.createdAt ? formatDate(selectedRisk.createdAt) : '-'}</span>
                <span>Last Modified: {selectedRisk.lastModified ? formatDate(selectedRisk.lastModified) : '-'}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Justification Modal */}
      <Modal
        isOpen={showJustificationModal}
        onClose={() => setShowJustificationModal(false)}
        title="Edit Justification"
        subtitle={currentControl?.control}
        size="lg"
        gradient="from-amber-600 to-orange-600"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">{currentControl?.objective}</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Justification for non-selection
              </label>
              <div className="flex items-center gap-2">
                {adminSnap.aiAssistEnabled && currentControl && (
                  <AiSuggestButton
                    type="soa-justification"
                    context={{
                      control: currentControl.control,
                      objective: currentControl.objective,
                    }}
                    onSuggestion={(text) => setJustificationText(text)}
                    label="AI Suggest"
                  />
                )}
                {/* Standard Wording button hidden — functionality preserved for future use
                <button
                  onClick={() => {
                    setWordingPickerType('justification');
                    setShowWordingPicker(true);
                  }}
                  className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
                >
                  <Icon name="FileText" size={14} />
                  Select Standard Wording
                </button>
                */}
              </div>
            </div>
            <textarea
              value={justificationText}
              onChange={(e) => setJustificationText(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder="Explain why this control is not applicable..."
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button onClick={saveJustification} className="flex-1">
              Save Justification
            </Button>
            <Button variant="secondary" onClick={() => setShowJustificationModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Standard Wording Picker Modal */}
      <Modal
        isOpen={showWordingPicker}
        onClose={() => setShowWordingPicker(false)}
        title={`Select Standard ${wordingPickerType === 'implementation' ? 'Implementation' : 'Justification'} Wording`}
        subtitle="Click to insert text that can be edited"
        size="lg"
        gradient={wordingPickerType === 'implementation' ? 'from-blue-600 to-indigo-600' : 'from-amber-600 to-orange-600'}
      >
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {(wordingPickerType === 'implementation' ? getImplementationWording() : getJustificationWording(currentControl?.control)).map((wording) => (
            <button
              key={wording.id}
              onClick={() => {
                if (wordingPickerType === 'justification') {
                  setJustificationText(wording.text);
                }
                setShowWordingPicker(false);
              }}
              className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <p className="font-medium text-gray-900 mb-1">{wording.name}</p>
              <p className="text-sm text-gray-600">{wording.text}</p>
              {wording.controls.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {wording.controls.map(c => (
                    <span key={c} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                      {c.split(' ')[0]}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
          {(wordingPickerType === 'implementation' ? getImplementationWording() : getJustificationWording(currentControl?.control)).length === 0 && (
            <p className="text-center text-gray-500 py-8">
              No standard wording defined{currentControl ? ' for this control' : ''}. Add entries in Admin → Standard Wording.
            </p>
          )}
        </div>
      </Modal>

      {/* Preview Modal - PDF-style layout */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Statement of Applicability"
        subtitle="PDF Preview"
        size="xl"
        gradient="from-indigo-600 to-purple-600"
      >
        <div className="bg-white p-8 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Header - Centered like PDF */}
          <div className="text-center space-y-2 border-b pb-6">
            {soaSnap.companyInfo.logo && (
              <img
                src={soaSnap.companyInfo.logo}
                alt="Company logo"
                className="h-12 max-w-[160px] object-contain mx-auto mb-2"
              />
            )}
            <h1 className="text-3xl font-bold text-indigo-600">Statement of Applicability</h1>
            <h2 className="text-xl text-gray-600">{soaSnap.companyInfo.companyName}</h2>
          </div>

          {/* Meta Info - Two columns like PDF */}
          <div className="flex justify-between text-sm text-gray-600">
            <span>Issue No: {soaSnap.companyInfo.issueNo}</span>
            <span>Date: {soaSnap.companyInfo.date || formatToday()}</span>
          </div>

          {/* Signatories */}
          {(soaSnap.companyInfo.preparedBy || soaSnap.companyInfo.reviewedBy || soaSnap.companyInfo.approvedBy) && (
            <div className="text-xs text-gray-500">
              {[
                soaSnap.companyInfo.preparedBy ? 'Prepared By: ' + soaSnap.companyInfo.preparedBy : '',
                soaSnap.companyInfo.reviewedBy ? 'Reviewed By: ' + soaSnap.companyInfo.reviewedBy : '',
                soaSnap.companyInfo.approvedBy ? 'Approved By: ' + soaSnap.companyInfo.approvedBy + (soaSnap.companyInfo.approvedDate ? ' (' + formatDate(soaSnap.companyInfo.approvedDate) + ')' : '') : '',
              ].filter(Boolean).join('  |  ')}
            </div>
          )}
          {soaSnap.companyInfo.nextReviewDate && (
            <div className="text-xs text-gray-500">
              Next Review: {formatDate(soaSnap.companyInfo.nextReviewDate)}
            </div>
          )}

          {/* Stats Summary - Inline like PDF */}
          <div className="flex gap-8 text-sm border-b pb-4">
            <div>
              <span className="font-semibold">Total:</span> {stats.totalControls}
            </div>
            <div>
              <span className="font-semibold">Applicable:</span> {stats.usedControls}
            </div>
            <div>
              <span className="font-semibold">Not Applicable:</span> {stats.unusedControls}
            </div>
            <div>
              <span className="font-semibold">Coverage:</span> {stats.coveragePercent}%
            </div>
            <div>
              <span className="font-semibold">Implemented:</span> {stats.implementedCount}
            </div>
          </div>

          {/* Controls Table - PDF style with proper borders */}
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-indigo-600 text-white">
                <tr>
                  <th className="px-3 py-3 text-left border border-gray-400 font-semibold" style={{ width: '15%' }}>Control</th>
                  <th className="px-3 py-3 text-left border border-gray-400 font-semibold" style={{ width: '22%' }}>Objective</th>
                  <th className="px-3 py-3 text-center border border-gray-400 font-semibold" style={{ width: '8%' }}>Applicable</th>
                  <th className="px-3 py-3 text-left border border-gray-400 font-semibold" style={{ width: '12%' }}>Risks</th>
                  <th className="px-3 py-3 text-left border border-gray-400 font-semibold" style={{ width: '30%' }}>Control Implementation</th>
                  <th className="px-3 py-3 text-center border border-gray-400 font-semibold" style={{ width: '13%' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {iso27001Controls.map((control, idx) => {
                  const risks = getRisksForControl(control.control);
                  const isUsed = risks.length > 0;
                  const justification = soaSnap.justifications[control.control];

                  return (
                    <tr key={control.control} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-3 py-3 border border-gray-300 text-xs align-top font-medium">{control.control}</td>
                      <td className="px-3 py-3 border border-gray-300 text-xs align-top text-gray-700">{control.objective}</td>
                      <td className="px-3 py-3 border border-gray-300 text-center align-top">
                        <span className={'px-2 py-1 rounded text-xs font-medium ' + (isUsed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
                          {isUsed ? 'Yes' : 'No'}
                        </span>
                      </td>
                      {isUsed ? (
                        <>
                          <td className="px-3 py-3 border border-gray-300 text-xs align-top" colSpan={3}>
                            {/* Inner table for multiple risks - NO borders */}
                            <table className="w-full">
                              <tbody>
                                {risks.map((r, riskIdx) => {
                                  const matchEntry = r.controls?.find(c => c.control === control.control);
                                  const implText = matchEntry?.controlImplementation || r.controlImplementation || '-';
                                  return (
                                  <tr key={r.id} className={riskIdx > 0 ? 'border-t border-gray-200' : ''}>
                                    <td className="py-1 pr-3 align-top text-gray-700" style={{ width: '20%' }}>
                                      {r.referenceNumber}
                                    </td>
                                    <td className="py-1 px-3 align-top text-gray-600" style={{ width: '55%' }}>
                                      {implText}
                                    </td>
                                    <td className="py-1 pl-3 align-top text-center" style={{ width: '25%' }}>
                                      <span className={'px-2 py-1 rounded text-xs whitespace-nowrap ' + 
                                        (r.controlStatus === 'Implemented' ? 'bg-green-100 text-green-700' : 
                                         r.controlStatus === 'In Progress' ? 'bg-blue-100 text-blue-700' : 
                                         r.controlStatus === 'Under Review' ? 'bg-yellow-100 text-yellow-700' : 
                                         'bg-gray-100 text-gray-700')
                                      }>
                                        {r.controlStatus || 'Not Started'}
                                      </span>
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-3 border border-gray-300 text-xs align-top text-gray-500">-</td>
                          <td className="px-3 py-3 border border-gray-300 text-xs align-top text-gray-600">
                            {justification?.justification || 'Not identified as managing any risks'}
                          </td>
                          <td className="px-3 py-3 border border-gray-300 text-center align-top">
                            <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">Not Applicable</span>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer - PDF style */}
          <div className="flex justify-between text-xs text-gray-500 pt-4 border-t">
            <span>Created using Certain® — Asset Risk Manager</span>
            <span>Generated on {formatToday()} | ISO 27001:2022 Statement of Applicability</span>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
            <Button onClick={handleExportPDF}>
              <Icon name="Download" size={16} className="mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </Modal>

      {/* Document Version Modal (shared component) */}
      <DocumentVersionModal
        isOpen={showCompanyInfoModal}
        onClose={() => setShowCompanyInfoModal(false)}
        onSave={() => showToast('Document version saved', 'success')}
      />

      <ToastContainer />
      </div>
    </div>
  );
}

// Control Card Component
function ControlCard({
  control,
  risks,
  isUsed,
  status,
  justification,
  onViewRisk,
  onEditJustification,
  onDeleteJustification,
}: {
  control: ISO27001Control;
  risks: Risk[];
  isUsed: boolean;
  status: { status: string; color: string };
  justification?: string;
  onViewRisk?: (risk: Risk) => void;
  onEditJustification: () => void;
  onDeleteJustification: () => void;
}) {
  const statusColors: Record<string, string> = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    gray: 'bg-gray-100 text-gray-600',
  };

  return (
    <div
      className={
        'border rounded-lg p-4 ' +
        (isUsed ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50')
      }
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span
              className={
                'px-2 py-1 rounded text-xs font-medium ' +
                (isUsed ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600')
              }
            >
              {isUsed ? 'Applicable' : 'Not Applicable'}
            </span>
            {isUsed && (
              <span className={'px-2 py-1 rounded text-xs font-medium ' + statusColors[status.color]}>
                {status.status}
              </span>
            )}
            {risks.length > 0 && (
              <span className="text-xs text-gray-500">
                {risks.length} risk{risks.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <h4 className="font-semibold text-gray-900">{control.control}</h4>
          <p className="text-sm text-gray-600 mt-1">{control.objective}</p>

          {/* Show risks using this control */}
          {risks.length > 0 && onViewRisk && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-2">Risks managed:</p>
              <div className="flex flex-wrap gap-2">
                {risks.map((risk) => (
                  <button
                    key={risk.id}
                    onClick={() => onViewRisk(risk)}
                    className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-purple-600 hover:bg-purple-50 transition"
                  >
                    {risk.referenceNumber}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Show justification for unused controls */}
          {!isUsed && justification && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-1">Justification:</p>
              <p className="text-sm text-gray-700">{justification}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-4">
          {!isUsed && (
            <>
              <button
                onClick={onEditJustification}
                className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition"
                title="Add Justification"
              >
                <Icon name="Edit" size={18} />
              </button>
              {justification && (
                <button
                  onClick={onDeleteJustification}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                  title="Remove justification"
                >
                  <Icon name="Trash" size={18} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
